import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../../../overmind';
import { DataPoint } from '../../../../../components/charts/recharts.models';
import { transformScore } from '../../../ranked-entities/ranked-entities.helpers';
import styles from './performance-overview.module.css';
import { IPerformanceOverviewWidgetProps } from './performance-overview.models';
import { AssessmentSearchParams, PermissionSearchParams, FilteringSearchParams, PagingSearchParams } from '../../../../../models/overmind/search-params';
import { HorizontalBarChart } from '../../../../../components/charts/horizontal-bar-chart/horizontal-bar-chart';
import { EmptyState } from '../../../../../components/general/empty-state/empty-state';
import { AnalyticsFilters } from '../../../analytics-filters/analytics-filters';
import { SkeletonOverlay } from '../../../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../analytics-filters/widget-card';
import { SelectedFilterOption } from '../../../analytics-filters/analytics-filters.models';
import { FiltersOutlet } from '../../../analytics-filters/filters-outlet';
import { getSelectedFilters, removeFilterOption } from '../../../analytics-filters/analytics-filters.helpers';
import { DEFAULT_FILTERING_SEARCH_PARAMS } from '../../../../../library/consts/consts';

const DEFAULT_SEARCH_PARAMS: PagingSearchParams = {
  page: 1,
  pageSize: 5,
};

export function PerformanceOverviewWidget({ baseSearchParams, departmentOptions, teamOptions, roleOptions, countryOptions }: IPerformanceOverviewWidgetProps) {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteringSearchParams, setFilteringSearchParams] = useState<FilteringSearchParams>(DEFAULT_FILTERING_SEARCH_PARAMS);
  const [highestDataPoints, setHighestDataPoints] = useState<DataPoint[]>([]);
  const [lowestDataPoints, setLowestDataPoints] = useState<DataPoint[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);

  async function getData(request: AssessmentSearchParams & PermissionSearchParams & FilteringSearchParams & PagingSearchParams) {
    setIsLoading(true);
    const response = await actions.getAssessmentPerformanceOverview(request);
    setHighestDataPoints(transformScore(response?.highest ?? [], companyVariables));
    setLowestDataPoints(transformScore(response?.lowest ?? [], companyVariables));
    setIsLoading(false);
  }

  useEffect(() => {
    getData({ ...baseSearchParams, ...DEFAULT_SEARCH_PARAMS, ...filteringSearchParams });
  }, [baseSearchParams]);

  return (
    <SkeletonOverlay isLoading={isLoading}>
      <WidgetCard title="Assessment performance overview" subtitle="An overview of assessment performance, highlighting individuals who achieved the highest scores and those with opportunities for improvement." showSkillLevelHelp>
        <div>
          <AnalyticsFilters
            teamOptions={teamOptions}
            departmentOptions={departmentOptions}
            roleOptions={roleOptions}
            countryOptions={countryOptions}
            filteringSearchParams={filteringSearchParams}
            setFilteringSearchParams={setFilteringSearchParams}
            applyFilters={() => {
              const selectedFilters = getSelectedFilters({ searchParams: filteringSearchParams, departmentOptions, teamOptions, roleOptions, countryOptions });
              setSelectedFilters(selectedFilters);
              getData({ ...baseSearchParams, ...DEFAULT_SEARCH_PARAMS, ...filteringSearchParams });
            }}
            clearFilters={() => {
              setFilteringSearchParams(DEFAULT_FILTERING_SEARCH_PARAMS);
              getData({ ...baseSearchParams, ...DEFAULT_SEARCH_PARAMS, ...DEFAULT_FILTERING_SEARCH_PARAMS });
            }}
          />
          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              setIsLoading(true);
              const updatedFilteringParams = removeFilterOption(filteringSearchParams, filter);
              setFilteringSearchParams(updatedFilteringParams);
              setSelectedFilters(prevSelectedFilters => prevSelectedFilters.filter(option => option.slug !== filter.slug));
              await getData({ ...baseSearchParams, ...DEFAULT_SEARCH_PARAMS, ...updatedFilteringParams });
              setIsLoading(false);
            }}
          />
        </div>

        <div className={styles.charts}>
          <div className={styles.chart}>
            <h4>Highest scoring participants</h4>
            {!!highestDataPoints.length ? <HorizontalBarChart fill="grape" dataPoints={highestDataPoints} /> : <EmptyState title="Hang tight!" subtitle="We are waiting for data." />}
          </div>

          <div className={styles.chart}>
            <h4>Lowest scoring participants</h4>
            {!!lowestDataPoints.length ? <HorizontalBarChart fill="primary" dataPoints={lowestDataPoints} /> : <EmptyState title="Hang tight!" subtitle="We are waiting for data." />}
          </div>
        </div>
      </WidgetCard>
    </SkeletonOverlay>
  );
}
